import { z } from "zod"

import {
  BillsAgentConfigSchema,
  StatementsAgentConfigSchema,
} from "../../agents/schemas.js"
import {
  agentActionTypes,
  agentGateNames,
  agentKinds,
  agentStatuses,
  agentSystemPauseReasons,
} from "../../agents/vocabulary.js"

/**
 * The agents surface. Single definition of the wire shapes: the API routes
 * import these and the api-client parses responses through them, so the two
 * cannot drift. No copy of any of this may exist in apps/api; that pattern
 * in document.schemas.ts is the known drift trap.
 */

export const ApiAgentKindSchema = z.enum(agentKinds)

export type ApiAgentKind = z.infer<typeof ApiAgentKindSchema>

export const ApiAgentStatusSchema = z.enum(agentStatuses)

export type ApiAgentStatus = z.infer<typeof ApiAgentStatusSchema>

export const ApiAgentActionTypeSchema = z.enum(agentActionTypes)

export type ApiAgentActionType = z.infer<typeof ApiAgentActionTypeSchema>

/**
 * One gate's frozen evidence. The same five-field reading the payables
 * evidence panel uses: what was compared, with which values, and the
 * sentence a person reads when it failed.
 */
export const ApiAgentGateEvidenceSchema = z.object({
  /** Every kind's gates, not one kind's: a schema pinned to the bills gate
   * names would reject the statements agent's own evidence on the wire. */
  gate: z.enum(agentGateNames),
  outcome: z.enum(["passed", "failed", "not_available"]),
  label: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type ApiAgentGateEvidence = z.infer<typeof ApiAgentGateEvidenceSchema>

/** Each config on the wire is the domain schema itself, not a copy. */
export const ApiBillsAgentConfigSchema = BillsAgentConfigSchema

export const ApiStatementsAgentConfigSchema = StatementsAgentConfigSchema

/**
 * One kind's config, whichever kind it is. The arms have no field in common,
 * so the union is unambiguous, and the registry's schema for the row's kind
 * is what the route and the engine parse against.
 */
export const ApiAgentConfigSchema = z.union([
  ApiBillsAgentConfigSchema,
  ApiStatementsAgentConfigSchema,
])

export type ApiAgentConfig = z.infer<typeof ApiAgentConfigSchema>

export const ApiAgentSchema = z.object({
  agentKind: ApiAgentKindSchema,
  /** Registry copy, so the settings card renders the kind it was handed. */
  name: z.string(),
  description: z.string(),
  status: ApiAgentStatusSchema,
  config: ApiAgentConfigSchema,
  systemPausedReason: z.enum(agentSystemPauseReasons).nullable(),
  systemPausedAt: z.string().datetime().nullable(),
  consecutiveFailures: z.number().int().nonnegative(),
  /** Null until an admin has written the config for the first time. */
  updatedAt: z.string().datetime().nullable(),
  /** The last 7 days, for the status strip and the watch banner. */
  activity: z.object({
    approvedCount: z.number().int().nonnegative(),
    heldCount: z.number().int().nonnegative(),
    wouldApproveCount: z.number().int().nonnegative(),
  }),
})

export type ApiAgent = z.infer<typeof ApiAgentSchema>

export const ApiAgentsResponseSchema = z.object({
  agents: z.array(ApiAgentSchema),
  /** False when the plan lost the entitlement; the page renders locked. */
  entitled: z.boolean(),
  /** True on a trial: the card shows the upgrade prompt, not the status
   * segment, and the route refuses a status past off. */
  trialing: z.boolean(),
})

export type ApiAgentsResponse = z.infer<typeof ApiAgentsResponseSchema>

/**
 * A config write carries the whole card: status and every control. Partial
 * writes would make "what did the admin see when they saved" unanswerable
 * in the audit trail.
 */
export const ApiUpdateAgentBodySchema = z.object({
  status: ApiAgentStatusSchema,
  config: ApiAgentConfigSchema,
})

export type ApiUpdateAgentBody = z.infer<typeof ApiUpdateAgentBodySchema>

export const ApiAgentActionSchema = z.object({
  agentActionId: z.string().uuid(),
  agentKind: ApiAgentKindSchema,
  /** Null when the document was deleted after the action was recorded. */
  documentId: z.string().uuid().nullable(),
  documentTitle: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  totalCents: z.number().int().nullable(),
  currency: z.string().nullable(),
  action: ApiAgentActionTypeSchema,
  /** Frozen at decision time: an audit answer must not change when the data
   * changes later. */
  reasons: z.array(ApiAgentGateEvidenceSchema),
  createdAt: z.string().datetime(),
})

export type ApiAgentAction = z.infer<typeof ApiAgentActionSchema>

export const ApiAgentActivityQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  cursor: z.string().max(200).optional(),
})

export type ApiAgentActivityQuery = z.infer<typeof ApiAgentActivityQuerySchema>

export const ApiAgentActivityResponseSchema = z.object({
  actions: z.array(ApiAgentActionSchema),
  nextCursor: z.string().nullable(),
})

export type ApiAgentActivityResponse = z.infer<
  typeof ApiAgentActivityResponseSchema
>
